import { useCallback, useMemo } from "react";

import { useSnackbar } from "notistack";
import { UseFormReturn } from "react-hook-form";

import { EmailContentBuilderType } from "../../EmailEditor/useGetEmailContent";
import { EmailContentFormData } from "../types";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { sanitizeWaypointSubmitPayload } from "@/features/EmailBuilder/helpers/sanitizeWaypointSubmitPayload";

interface UseSaveBodyProps {
  form: UseFormReturn<EmailContentFormData>;
  emailContent: EmailContentBuilderType | null | undefined;
  setIsLoading: (loading: boolean) => void;
  refetchEmailContent: () => Promise<void>;
  onEmailContentDeleted: () => void;
}

export const useSaveBody = ({
  form,
  emailContent,
  setIsLoading,
  refetchEmailContent,
  onEmailContentDeleted,
}: UseSaveBodyProps) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: updateEmailBodyMutation } = trpc.updateEmailBody.useMutation();

  const onSubmit = useCallback(
    async (data: EmailContentFormData) => {
      if (!emailContent?.id) {
        return;
      }

      setIsLoading(true);

      const { sanitizedDesignConfig, sanitizedBody, didSanitize } = sanitizeWaypointSubmitPayload({
        body: data.body,
        designConfig: data.designConfig,
        editorVersion: data.editorVersion,
      });
      if (didSanitize) {
        form.setValue("designConfig", sanitizedDesignConfig as Record<string, string | number>, { shouldDirty: false });
        form.setValue("body", sanitizedBody, { shouldDirty: false });
      }

      const { error } = await updateEmailBodyMutation({
        emailContentId: emailContent.id,
        body: sanitizedBody,
        subject: data.subject,
        attachmentUrl: data.attachment?.fileUrl,
        designConfig: sanitizedDesignConfig ?? undefined,
        editorVersion: data.editorVersion ?? undefined,
      });

      if (error) {
        setIsLoading(false);
        if (error.code === "email_content_not_found") {
          onEmailContentDeleted();
          return;
        }
        enqueueSnackbar({ vibe: "danger", message: t("dashboard", "email.save.error") });
        throw new Error(`updateEmailBody failed: ${error.code}`);
      }
      await refetchEmailContent();

      setIsLoading(false);
    },
    [
      emailContent?.id,
      setIsLoading,
      updateEmailBodyMutation,
      enqueueSnackbar,
      t,
      refetchEmailContent,
      form,
      onEmailContentDeleted,
    ]
  );

  const save = useMemo(() => form.handleSubmit(onSubmit), [form, onSubmit]);

  return { save };
};
